import React, { useEffect, useRef, useState } from 'react';
import Icon from '../components/Icon';

const REFRESH_MS = 60_000;

const stateMeta = {
  live:    { color: 'var(--red)',    label: 'LIVE',    dot: '●' },
  pending: { color: 'var(--text3)',  label: 'PENDING', dot: '○' },
  final:   { color: 'var(--green)',  label: 'FINAL',   dot: '✓' },
};

const comebackMeta = {
  imminent: { color: 'var(--red)',    label: 'COMEBACK LIVE',  glow: '0 0 16px rgba(255,71,87,0.45)' },
  active:   { color: 'var(--accent2)',label: 'CLOSING IN',     glow: '0 0 12px rgba(var(--accent2-rgb),0.30)' },
  watching: { color: 'var(--yellow)', label: 'WATCHING',       glow: 'none' },
};

function ScorePill({ side, isLeader }) {
  return (
    <div style={{
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        color: isLeader ? 'var(--accent)' : 'var(--text)',
        fontWeight: isLeader ? 700 : 600,
        fontSize: '0.9rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {isLeader && <span style={{ fontSize: '0.7rem' }}>▶</span>}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {side.display_name}
        </span>
      </div>
      <div style={{
        fontFamily: 'var(--font-display)',
        fontSize: '1.8rem',
        lineHeight: 1,
        color: isLeader ? 'var(--accent)' : 'var(--text)',
      }}>
        {side.points.toFixed(1)}
      </div>
      <div style={{
        fontSize: '0.65rem',
        color: 'var(--text3)',
        fontFamily: 'var(--font-mono)',
      }}>
        {side.starters_played}/{side.starters_total} played
        {side.starters_remaining > 0 && ` · ${side.starters_remaining} to go`}
      </div>
    </div>
  );
}

function MatchupRow({ pair }) {
  const sm = stateMeta[pair.state] || stateMeta.pending;
  const cm = comebackMeta[pair.comeback?.severity];
  const accentColor = cm ? cm.color : sm.color;
  return (
    <div style={{
      background: 'var(--bg2)',
      border: '1px solid var(--border)',
      borderLeft: `3px solid ${accentColor}`,
      borderRadius: 'var(--radius)',
      padding: '14px 16px',
      boxShadow: cm?.glow !== 'none' && cm?.glow ? cm.glow : undefined,
      transition: 'box-shadow 0.4s ease',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
      }}>
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          letterSpacing: '0.2em',
          color: sm.color,
          textTransform: 'uppercase',
        }}>
          {sm.dot} {sm.label}
        </span>
        {cm && (
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '0.2em',
            padding: '3px 8px',
            borderRadius: 4,
            color: cm.color,
            background: `${cm.color}1a`,
            border: `1px solid ${cm.color}55`,
          }}>
            <Icon emoji="⚡" size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />{cm.label}
          </span>
        )}
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 60px 1fr',
        gap: 12,
        alignItems: 'center',
      }}>
        <ScorePill side={pair.a} isLeader={pair.leader === 'a'} />
        <div style={{
          textAlign: 'center',
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          color: 'var(--text3)',
          letterSpacing: '0.2em',
        }}>
          <div>VS</div>
          {pair.margin > 0 && (
            <div style={{ fontSize: 10, color: 'var(--text2)', marginTop: 4 }}>
              {pair.margin.toFixed(1)} pt
            </div>
          )}
        </div>
        <ScorePill side={pair.b} isLeader={pair.leader === 'b'} />
      </div>
      {pair.comeback?.narrative && (
        <div style={{
          marginTop: 10,
          paddingTop: 10,
          borderTop: '1px solid var(--border)',
          fontSize: 12,
          color: 'var(--text2)',
          fontStyle: 'italic',
        }}>
          {pair.comeback.narrative}
        </div>
      )}
    </div>
  );
}

export default function GameDay({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [posting, setPosting] = useState(false);
  const [postMsg, setPostMsg] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastFetch, setLastFetch] = useState(null);
  const intervalRef = useRef(null);

  const load = async () => {
    try {
      const r = await fetch(`${api}/api/gameday`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      setData(json);
      setLastFetch(new Date());
      setError(null);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Auto-refresh every 60s while enabled
  useEffect(() => {
    if (!autoRefresh) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => load(), REFRESH_MS);
    return () => intervalRef.current && clearInterval(intervalRef.current);
    /* eslint-disable-next-line */
  }, [autoRefresh]);

  const handlePost = async () => {
    setPosting(true);
    setPostMsg(null);
    try {
      const r = await fetch(`${api}/api/gameday/post`, { method: 'POST' });
      const j = await r.json();
      if (j.status === 'posted') {
        setPostMsg({ type: 'success', text: 'Posted to Discord' });
      } else {
        setPostMsg({ type: 'error', text: j.error || 'Post failed' });
      }
    } catch (e) {
      setPostMsg({ type: 'error', text: e.message || 'Post failed' });
    }
    setPosting(false);
    setTimeout(() => setPostMsg(null), 5000);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
        <span>Loading live scoreboard...</span>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <div className="page-header">
          <div className="page-title">Game Day</div>
          <div className="page-subtitle">Live matchup scoring</div>
        </div>
        <div className="empty-state">
          <p>Could not load gameday data: {error}</p>
        </div>
      </div>
    );
  }

  const pairs = data?.pairs || [];

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Game Day</div>
        <div className="page-subtitle">
          Week {data?.week} · {data?.season} · Live updates every 60s
        </div>
      </div>

      {/* Status banner */}
      <div style={{
        background: 'var(--bg2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: '14px 18px',
        marginBottom: 18,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '0.2em',
            color: 'var(--text3)',
            textTransform: 'uppercase',
            marginBottom: 4,
          }}>
            Live Status
          </div>
          <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600 }}>
            {data?.headline || '—'}
          </div>
          {lastFetch && (
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
              Updated {lastFetch.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={() => setAutoRefresh((x) => !x)}
            className="btn"
            style={{
              background: autoRefresh ? 'rgba(0, 230, 118, 0.12)' : 'var(--bg4)',
              color: autoRefresh ? 'var(--green)' : 'var(--text2)',
              border: `1px solid ${autoRefresh ? 'rgba(0, 230, 118, 0.3)' : 'var(--border)'}`,
            }}
            title={autoRefresh ? 'Auto-refresh on' : 'Auto-refresh off'}
          >
            <Icon emoji={autoRefresh ? '⏸' : '▶'} size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Auto-refresh
          </button>
          <button onClick={load} className="btn btn-refresh"><Icon emoji="↻" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Refresh</button>
          <button
            onClick={handlePost}
            disabled={posting || pairs.length === 0}
            className="btn btn-discord"
          >
            <Icon emoji={posting ? '⏳' : '📣'} size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{posting ? 'Posting...' : 'Post to Discord'}
          </button>
        </div>
      </div>

      {data?.upstream_stale && (
        <div role="status" style={{
          marginTop: -8,
          marginBottom: 18,
          padding: '9px 12px',
          borderRadius: 6,
          border: '1px solid rgba(255, 193, 7, 0.35)',
          background: 'rgba(255, 193, 7, 0.10)',
          color: 'var(--text2)',
          fontSize: 12,
        }}>
          Showing last-known-good Sleeper data while the upstream service recovers.
        </div>
      )}

      {postMsg && (
        <div style={{
          marginBottom: 14,
          padding: '10px 14px',
          borderRadius: 'var(--radius)',
          background: postMsg.type === 'success' ? 'rgba(0,230,118,0.08)' : 'rgba(255,71,87,0.08)',
          border: `1px solid ${postMsg.type === 'success' ? 'rgba(0,230,118,0.3)' : 'rgba(255,71,87,0.3)'}`,
          color: postMsg.type === 'success' ? 'var(--green)' : 'var(--red)',
          fontSize: 13,
          fontFamily: 'var(--font-mono)',
        }}>
          <Icon emoji={postMsg.type === 'success' ? '✓' : '✕'} size={13} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          {postMsg.text}
        </div>
      )}

      {pairs.length === 0 ? (
        <div className="empty-state">
          <p>No matchups scheduled for this week.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {pairs.map((p) => (
            <MatchupRow key={p.matchup_id} pair={p} />
          ))}
        </div>
      )}
    </div>
  );
}
